import * as React from 'react';
import {
  PiArrowSquareOut,
  PiCaretDown,
  PiCaretLeft,
  PiCaretRight,
  PiX,
} from 'react-icons/pi';
import { cn } from '@ais-ui/utils';
import { Badge } from '../Badge';
import { Button } from '../Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../DropdownMenu';
import { useTableDockContext } from './context';

/* ------------------------------------------------------------------ */
/* Zone 3: Controls                                                    */
/* ------------------------------------------------------------------ */

const formatCount = (value: number): string => value.toLocaleString();

export const TableDockControls = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <div
    className={cn('flex flex-none items-center gap-2', className)}
    {...props}
  />
);

export interface ActionsProps {
  /** Total rows in the current (filtered) result — scopes the menu when nothing is selected. */
  resultCount?: number;
  /** Trigger label. Always "Actions" by design; override only if truly needed. */
  label?: React.ReactNode;
  /** Menu width in px (design default 226). */
  menuWidthPx?: number;
  align?: 'start' | 'center' | 'end';
  children?: React.ReactNode;
}

export const TableDockActions = ({
  resultCount,
  label = 'Actions',
  menuWidthPx = 226,
  align = 'end',
  children,
}: ActionsProps) => {
  const { selectedCount } = useTableDockContext('TableDock.Actions');
  const hasSelection = selectedCount > 0;

  const scopeHeading = hasSelection
    ? `${formatCount(selectedCount)} selected`
    : resultCount !== undefined
      ? `${formatCount(resultCount)} rows in this result`
      : undefined;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-[26px] gap-1 px-2.5 text-xs font-medium"
        >
          {label}
          {hasSelection && (
            <Badge className="flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none">
              {formatCount(selectedCount)}
            </Badge>
          )}
          <PiCaretDown className="size-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        style={{ width: menuWidthPx }}
        className="rounded-lg p-1"
      >
        {scopeHeading && (
          <div className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
            {scopeHeading}
          </div>
        )}
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export interface ActionItemProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuItem
> {
  /** Leading glyph (15px, muted-foreground). */
  icon?: React.ReactNode;
}

export const TableDockActionItem = ({
  icon,
  children,
  className,
  ...props
}: ActionItemProps) => (
  <DropdownMenuItem
    className={cn('h-[30px] rounded-md text-[13px] font-normal', className)}
    {...props}
  >
    {icon && (
      <span className="flex size-[15px] items-center justify-center text-muted-foreground [&_svg]:size-[15px]">
        {icon}
      </span>
    )}
    {children}
  </DropdownMenuItem>
);

export const TableDockActionSeparator = DropdownMenuSeparator;

export const TableDockPrimaryAction = ({
  className,
  ...props
}: React.ComponentProps<typeof Button>) => (
  <Button className={cn('h-[26px] px-3 text-xs', className)} {...props} />
);

export interface PagerProps {
  /** First visible row (1-based). Omit for count-only mode. */
  from?: number;
  /** Last visible row. Omit for count-only mode. */
  to?: number;
  /** Total rows (unfiltered). */
  total: number;
  /** Matched rows when a filter is applied; when < total the label shows "match". */
  matched?: number;
  /** Word used in count-only mode (defaults to "rows"). */
  unit?: string;
  onPrev?: () => void;
  onNext?: () => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
}

const buildPagerLabel = ({
  from,
  to,
  total,
  matched,
  unit = 'rows',
}: Pick<PagerProps, 'from' | 'to' | 'total' | 'matched' | 'unit'>): string => {
  const hasValidRange =
    from !== undefined && to !== undefined && from > 0 && to >= from;
  const range = hasValidRange
    ? `${formatCount(from)}–${formatCount(to)}`
    : undefined;

  const isFiltered = matched !== undefined && matched !== total;

  if (!range) {
    const count = isFiltered ? matched : total;
    return `${formatCount(count ?? total)} ${unit}`;
  }
  if (isFiltered) {
    return `${range} · ${formatCount(matched)} of ${formatCount(total)} match`;
  }
  return `${range} of ${formatCount(total)}`;
};

const iconButtonClasses =
  'flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:text-input';

export const TableDockPager = ({
  from,
  to,
  total,
  matched,
  unit,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
}: PagerProps) => {
  const hasPaging = onPrev !== undefined || onNext !== undefined;

  return (
    <div className="flex items-center gap-2">
      <span className="whitespace-nowrap text-xs font-normal tabular-nums text-muted-foreground">
        {buildPagerLabel({ from, to, total, matched, unit })}
      </span>
      {hasPaging && (
        <div className="flex items-center">
          <button
            type="button"
            onClick={onPrev}
            disabled={prevDisabled}
            aria-label="Previous page"
            className={iconButtonClasses}
          >
            <PiCaretLeft className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={nextDisabled}
            aria-label="Next page"
            className={iconButtonClasses}
          >
            <PiCaretRight className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export const TableDockPopOut = ({
  className,
  ...props
}: React.ComponentProps<'button'>) => {
  const { isDetached } = useTableDockContext('TableDock.PopOut');
  if (isDetached) {
    return null;
  }
  return (
    <button
      type="button"
      aria-label="Open in new window"
      className={cn(iconButtonClasses, className)}
      {...props}
    >
      <PiArrowSquareOut className="size-[15px]" />
    </button>
  );
};

export const TableDockClose = ({
  className,
  ...props
}: React.ComponentProps<'button'>) => (
  <button
    type="button"
    aria-label="Close table"
    className={cn(iconButtonClasses, className)}
    {...props}
  >
    <PiX className="size-[15px]" />
  </button>
);
